import { BrandMark } from '@iris/ui/components/brand-mark';
import { Button } from '@iris/ui/components/ui/button';
import { AlertTriangle, Compass } from 'lucide-react';
import type { ReactNode } from 'react';

/** Full-height centred message used by error and not-found screens. Texts are translated by the app. */
function CenteredMessage({
  icon,
  title,
  body,
  children,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  children?: ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-sm flex-col items-center justify-center gap-4 px-4 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {icon}
      </div>
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{body}</p>
      {children}
    </div>
  );
}

/** T4 §2.4 "5xx / unknown": generic message, retry, and the request id for support. */
export function ErrorScreen({
  title,
  body,
  retryLabel,
  onRetry,
  reference,
}: {
  title: string;
  body: string;
  retryLabel: string;
  onRetry: () => void;
  /** already translated, e.g. "Référence : 01a1…" */
  reference?: string;
}) {
  return (
    <CenteredMessage icon={<AlertTriangle aria-hidden className="h-6 w-6" />} title={title} body={body}>
      <Button onClick={onRetry}>{retryLabel}</Button>
      {reference ? (
        <p className="text-xs text-muted-foreground">
          <bdi dir="ltr">{reference}</bdi>
        </p>
      ) : null}
    </CenteredMessage>
  );
}

export function NotFoundScreen({
  title,
  body,
  homeLabel,
  onHome,
}: {
  title: string;
  body: string;
  homeLabel: string;
  onHome: () => void;
}) {
  return (
    <CenteredMessage icon={<Compass aria-hidden className="h-6 w-6" />} title={title} body={body}>
      <Button variant="outline" onClick={onHome}>
        {homeLabel}
      </Button>
    </CenteredMessage>
  );
}

/** Loading placeholder for lazy route chunks (T4 §1: routes are lazy-loaded). */
export function RouteFallback({ label }: { label: string }) {
  // ADR 0016: the logo inside a turning ring
  return (
    <div role="status" aria-live="polite" className="flex min-h-[40vh] items-center justify-center">
      <span className="sr-only">{label}</span>
      <span aria-hidden className="relative flex h-14 w-14 items-center justify-center">
        <span className="absolute inset-0 animate-spin rounded-full border-2 border-brand-100 border-t-primary motion-reduce:animate-none" />
        <BrandMark className="h-8 w-8" />
      </span>
    </div>
  );
}
